import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sun, Battery, Zap, Calculator, Star, ChevronLeft, ChevronRight, Check, ArrowRight, Search } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import SeoHead from './common/SeoHead';
import { getReviews } from '../services/reviewService';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import Stage6LoadsheddingSA from './articles/Stage6LoadsheddingSA';
import LoadSheddingSouthAfrica2025 from './articles/LoadSheddingSouthAfrica2025';
import SolarRegistrationLaws2025 from './articles/SolarRegistrationLaws2025';
import PerovskiteSolarBreakthrough from './articles/PerovskiteSolarBreakthrough';
import ElectricityPriceIncreaseSA2025 from './articles/ElectricityPriceIncreaseSA2025';

// Define the function outside the component so it's available before use
const getLatestNews = () => {
  return [
    {
      id: 'electricity-price-increase-south-africa-2025',
      title: 'Electricity Price Increase in South Africa: What You Need to Know',
      summary: 'Analysis of Eskom\'s 12.74% tariff hike in 2025, its impact on households and businesses, and how solar power offers an affordable alternative.',
      date: new Date('2025-02-25'),
      slug: 'electricity-price-increase-south-africa-2025',
      component: ElectricityPriceIncreaseSA2025
    },
    {
      id: 'stage-6-loadshedding-south-africa',
      title: 'Stage 6 Loadshedding Crisis in South Africa',
      summary: 'Analysis of Stage 6 loadshedding\'s impact, with 6,000MW cuts causing R500M daily losses. Explore solutions and recovery plans.',
      date: new Date('2025-02-15'),
      slug: 'stage-6-loadshedding-south-africa',
      component: Stage6LoadsheddingSA
    },
    {
      id: 'solar-registration-laws-2025',
      title: 'South Africa\'s New Solar Registration Laws',
      summary: 'Essential guide to 2025 solar registration requirements, deadlines, and compliance for homeowners.',
      date: new Date('2025-02-10'),
      slug: 'solar-registration-laws-2025',
      component: SolarRegistrationLaws2025
    },
    {
      id: 'load-shedding-south-africa-2025',
      title: 'Load Shedding in South Africa 2025',
      summary: 'Comprehensive analysis of the ongoing load shedding crisis, examining causes and solutions.',
      date: new Date('2025-01-31'),
      slug: 'load-shedding-south-africa-2025',
      component: LoadSheddingSouthAfrica2025
    }
  ];
};

const Home = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [latestNews, setLatestNews] = useState(getLatestNews());

  // Sample reviews with South African locations - replace with actual data later
  const sampleReviews = [
    {
      id: 1,
      userName: "David van der Merwe",
      location: "Cape Town, Western Cape",
      rating: 5,
      comment: "Incredible savings on our electricity bill since installing the solar system. Load shedding is no longer a concern for our family.",
      userAvatar: "/placeholder-avatar.svg",
      createdAt: new Date("2024-01-15")
    },
    {
      id: 2,
      userName: "Sarah Naidoo",
      location: "Durban, KwaZulu-Natal",
      rating: 5,
      comment: "The installation team was professional and efficient. Our home now has reliable power even during the worst load shedding schedules.",
      userAvatar: "/placeholder-avatar.svg",
      createdAt: new Date("2024-01-10")
    },
    {
      id: 3,
      userName: "John Botha",
      location: "Johannesburg, Gauteng",
      rating: 4,
      comment: "Great investment for our business. The solar panels have significantly reduced our operating costs.",
      userAvatar: "/placeholder-avatar.svg",
      createdAt: new Date("2024-01-05")
    },
    {
      id: 4,
      userName: "Thabo Molefe",
      location: "Pretoria, Gauteng",
      rating: 5,
      comment: "The solar calculator helped us choose the perfect system size. Now we're completely off-grid!",
      userAvatar: "/placeholder-avatar.svg",
      createdAt: new Date("2024-01-01")
    },
    {
      id: 5,
      userName: "Lisa Patel",
      location: "Port Elizabeth, Eastern Cape",
      rating: 4,
      comment: "Excellent customer service and the battery backup system has been a game-changer during load shedding.",
      userAvatar: "/placeholder-avatar.svg",
      createdAt: new Date("2023-12-28")
    },
    {
      id: 6,
      userName: "Michael Pretorius",
      location: "Bloemfontein, Free State",
      rating: 5,
      comment: "The whole process from consultation to installation was smooth. Very happy with the performance of our solar system.",
      userAvatar: "/placeholder-avatar.svg",
      createdAt: new Date("2023-12-20")
    }
  ];

  useEffect(() => {
    setReviews(sampleReviews);
    fetchTopProducts();
  }, []);

  const fetchTopProducts = async () => {
    try {
      const allReviews = await getReviews();
      
      // Sample top products (replace with actual data from your reviews)
      const sampleTopProducts = [
        {
          id: 1,
          name: "Canadian Solar HiKu Panels",
          category: "Solar Panels",
          avgRating: 4.8,
          reviewCount: 156,
          description: "High-efficiency panels perfect for residential use",
          price: "R8,500 - R12,000",
          topReview: {
            userName: "James Wilson",
            location: "Cape Town",
            rating: 5,
            comment: "These panels have exceeded our expectations. Power output is consistently high even on cloudy days.",
            verifiedPurchase: true
          }
        },
        {
          id: 2,
          name: "Tesla Powerwall 2",
          category: "Batteries",
          avgRating: 4.9,
          reviewCount: 98,
          description: "Premium home battery backup solution",
          price: "R15,000 - R25,000",
          topReview: {
            userName: "Sarah van Wyk",
            location: "Johannesburg",
            rating: 5,
            comment: "Complete peace of mind during load shedding. The app makes it easy to monitor power usage.",
            verifiedPurchase: true
          }
        },
        {
          id: 3,
          name: "Fronius Symo Inverter",
          category: "Inverters",
          avgRating: 4.7,
          reviewCount: 112,
          description: "Reliable three-phase inverter with smart features",
          price: "R12,000 - R18,000",
          topReview: {
            userName: "Michael Ndlovu",
            location: "Durban",
            rating: 5,
            comment: "Silent operation and excellent performance. The monitoring system is top-notch.",
            verifiedPurchase: true
          }
        }
      ];

      setTopProducts(sampleTopProducts);
    } catch (error) {
      console.error('Error fetching top products:', error);
    }
  };

  const nextReview = () => {
    setCurrentReviewIndex((prevIndex) => 
      prevIndex === reviews.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevReview = () => {
    setCurrentReviewIndex((prevIndex) => 
      prevIndex === 0 ? reviews.length - 1 : prevIndex - 1
    );
  };

  const features = [
    {
      icon: <Sun className="w-12 h-12" />,
      title: "Solar Solutions",
      description: "Find the perfect solar setup for your home",
      link: "/product/panels"
    },
    {
      icon: <Battery className="w-12 h-12" />,
      title: "Power Storage",
      description: "Explore battery storage options",
      link: "/product/batteries"
    },
    {
      icon: <Calculator className="w-12 h-12" />,
      title: "Solar Calculators",
      description: "Calculate your solar needs and savings",
      link: "/calculators/savings"
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Load Shedding",
      description: "Stay updated with load shedding schedules",
      link: "/load-shedding"
    }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Solar Insight South Africa",
    "description": "Your trusted source for solar energy solutions in South Africa. Expert guidance on solar panels, batteries, and load shedding solutions.",
    "url": "https://solarinsight.co.za",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "South Africa"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://solarinsight.co.za/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const StarRating = ({ rating }) => (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-5 h-5 ${
            star <= rating
              ? isDarkMode
                ? 'text-yellow-400'
                : 'text-amber-400'
              : 'text-gray-300'
          }`}
          fill={star <= rating ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );

  // SEO Configuration
  const seoData = {
    title: 'Solar Insight | Solar Power Solutions in South Africa',
    description: 'Leading provider of solar power solutions in South Africa. Expert installation, maintenance, and support for residential and commercial solar systems.',
    keywords: 'solar power, solar energy, solar installation, solar panels, South Africa solar, renewable energy, load shedding solutions',
    path: '/',
    image: '/placeholder-image.svg',
    features: [
      'Professional Installation',
      'Load Shedding Solutions',
      'Battery Backup Systems',
      'Solar Maintenance',
      'Energy Monitoring'
    ],
    services: [
      'Residential Solar',
      'Commercial Solar',
      'Battery Storage',
      'System Maintenance'
    ],
    coverage: [
      'Cape Town',
      'Johannesburg',
      'Durban',
      'Pretoria'
    ],
    stats: {
      installationsCompleted: 500,
      averageRating: 4.9,
      reviewCount: 250,
      yearsExperience: 10
    }
  };

  // Create a mapping of article slugs to components
  const articleComponents = {
    'stage-6-loadshedding-south-africa': Stage6LoadsheddingSA,
    'load-shedding-south-africa-2025': LoadSheddingSouthAfrica2025,
    'solar-registration-laws-2025': SolarRegistrationLaws2025,
    'perovskite-solar-breakthrough-2025': PerovskiteSolarBreakthrough
  };

  const popularSearches = useMemo(
    () => [
      { label: 'Panels', q: 'solar panels' },
      { label: 'Battery', q: 'battery' },
      { label: 'Savings', q: 'savings calculator' },
      { label: 'Load shedding', q: 'load shedding' }
    ],
    []
  );

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-blue-900 text-white' : 'bg-amber-50 text-gray-900'}`}>
      <SeoHead template="general" data={seoData} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className={`text-center py-16 ${isDarkMode ? 'bg-blue-800' : 'bg-amber-800'} rounded-lg shadow-xl mb-12`}>
          <h1 className={`
            text-4xl md:text-6xl font-bold mb-6
            ${isDarkMode ? 'text-yellow-400' : 'text-yellow-400'}
            drop-shadow-[0_0_10px_rgba(250,204,21,0.7)]
            transition-all duration-300
          `}>
            Power Your Future with Solar
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Find the perfect solar solution for your home with our comprehensive tools and calculators
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/calculators/panel"
              className={`
                px-6 py-3 rounded-lg font-semibold text-white
                ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-600 hover:bg-amber-500'}
                transition duration-200
                transform hover:scale-105
                shadow-lg hover:shadow-xl
                border-2 border-yellow-400
              `}
            >
              Get Started
            </Link>
            <Link
              to="/product/all"
              className={`
                px-6 py-3 rounded-lg font-semibold
                ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-700 hover:bg-amber-600'}
                text-yellow-400
                transition duration-200
                transform hover:scale-105
                shadow-lg hover:shadow-xl
                border-2 border-yellow-400
              `}
            >
              View Products
            </Link>
          </div>
        </div>

        {/* Search Section (under hero) */}
        <div
          className={`
            mb-12 rounded-lg shadow-xl p-6
            ${isDarkMode ? 'bg-blue-800/70' : 'bg-amber-800/70'}
          `}
        >
          <h2 className="text-xl md:text-2xl font-bold text-yellow-400 mb-4 text-center">
            Search Solar Insight
          </h2>

          <form onSubmit={handleSearchSubmit} className="max-w-3xl mx-auto">
            <label htmlFor="home-search" className="sr-only">
              Search the site
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-yellow-400" />
                <input
                  id="home-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Try: panels, battery calculator, load shedding, installation…"
                  aria-label="Search the site"
                  className={`
                    w-full pl-12 pr-4 py-3 rounded-lg
                    ${isDarkMode ? 'bg-blue-900/60 text-white placeholder:text-gray-300' : 'bg-amber-50 text-gray-900 placeholder:text-amber-900/60'}
                    border-2 border-yellow-400
                    focus:outline-none focus:ring-2 focus:ring-yellow-400/60
                    shadow-lg
                  `}
                />
              </div>
              <button
                type="submit"
                className={`
                  px-6 py-3 rounded-lg font-semibold
                  ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-600 hover:bg-amber-500'}
                  text-white
                  transition duration-200 transform hover:scale-[1.02]
                  shadow-lg hover:shadow-xl
                  border-2 border-yellow-400
                `}
              >
                Search
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
              <span className="text-gray-200">Popular:</span>
              {popularSearches.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    setSearchQuery(item.q);
                    navigate(`/search?q=${encodeURIComponent(item.q)}`);
                  }}
                  className={`
                    px-3 py-1 rounded-full
                    border border-yellow-400/80
                    ${isDarkMode ? 'bg-blue-900/40 hover:bg-blue-900/60' : 'bg-amber-50/80 hover:bg-amber-50'}
                    text-yellow-400
                    transition-colors duration-200
                  `}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </form>
        </div>

        {/* Features Section */}
        <div className="mb-12">
          <h2 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
            Solar Solutions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className={`
                  p-6 rounded-lg text-center shadow-xl
                  ${isDarkMode ? 'bg-blue-800 hover:bg-blue-700' : 'bg-amber-800 hover:bg-amber-700'}
                  transition duration-200
                `}
              >
                <div className="flex justify-center mb-4 text-yellow-400">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-300">
                  {feature.description}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* Latest Solar News Section */}
        <div className="mb-12">
          <h2 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
            Latest Solar News
          </h2>
          
          {/* Grid of news articles from components */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {latestNews.map((newsItem) => (
              <div 
                key={newsItem.id}
                className={`rounded-lg overflow-hidden shadow-xl transition-all duration-300 ${
                  isDarkMode ? 'bg-blue-800 hover:bg-blue-700' : 'bg-amber-100 hover:bg-amber-200'
                }`}
              >
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                    {newsItem.title}
                  </h3>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-amber-900'}`}>
                    {newsItem.summary}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm opacity-75">
                      {newsItem.date.toLocaleDateString('en-ZA', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                    <Link 
                      to={`/news/${newsItem.slug}`}
                      className={`flex items-center gap-1 font-medium ${
                        isDarkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-amber-700 hover:text-amber-600'
                      }`}
                    >
                      Read More <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/news"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300' 
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              }`}
            >
              <span className="font-semibold">View All News</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>

        {/* Full Latest Articles Section */}
        <div className="mt-16 mb-12">
          <h2 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
            Featured Solar Articles
          </h2>
          
          {/* Render the first article component */}
          {latestNews.length > 0 && (
            <div className="mb-10 p-6 rounded-lg shadow-xl transition-all duration-300
              ${isDarkMode ? 'bg-blue-800/60' : 'bg-amber-100/60'}">
              <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                {latestNews[0].title}
              </h3>
              
              <div className="border-l-4 border-yellow-400 pl-4 mb-6">
                <p className={`italic ${isDarkMode ? 'text-gray-300' : 'text-amber-900'}`}>
                  {latestNews[0].summary}
                </p>
                <div className="mt-2 text-sm">
                  <span className="opacity-75">
                    Published: {latestNews[0].date.toLocaleDateString('en-ZA', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
              
              {/* Render condensed version of the article */}
              <div className="article-content-preview">
                {/* This depends on how your article components are structured */}
                {/* Here's a simplified approach: */}
                <div className="mb-6">
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-200' : 'text-amber-900'}`}>
                    South Africa's ongoing load shedding crisis has reached unprecedented levels with Stage 6 implementation, 
                    causing widespread disruption to businesses and daily life. The 6,000MW power cut is costing the economy 
                    an estimated R500 million daily as industries struggle to maintain operations.
                  </p>
                  
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-200' : 'text-amber-900'}`}>
                    Key factors contributing to the crisis include aging infrastructure, coal supply issues, and delayed 
                    maintenance. The government's recovery plan focuses on expediting renewable energy projects and 
                    encouraging private sector participation.
                  </p>
                </div>
                
                <div className="text-center">
                  <Link 
                    to={`/news/${latestNews[0].slug}`}
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300' 
                        : 'bg-amber-500 text-white hover:bg-amber-600'
                    }`}
                  >
                    <span className="font-semibold">Read Full Article</span>
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>
          )}
          
          {/* Other recent articles in condensed format */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {latestNews.slice(1, 3).map((newsItem) => (
              <div 
                key={newsItem.id}
                className={`p-6 rounded-lg shadow-xl transition-all duration-300 ${
                  isDarkMode ? 'bg-blue-800/60' : 'bg-amber-100/60'
                }`}
              >
                <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-700'}`}>
                  {newsItem.title}
                </h3>
                
                <div className="border-l-4 border-yellow-400 pl-4 mb-4">
                  <p className={`italic text-sm ${isDarkMode ? 'text-gray-300' : 'text-amber-900'}`}>
                    {newsItem.summary}
                  </p>
                  <div className="mt-1 text-xs">
                    <span className="opacity-75">
                      {newsItem.date.toLocaleDateString('en-ZA', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
                
                <div className="text-right">
                  <Link 
                    to={`/news/${newsItem.slug}`}
                    className={`inline-flex items-center gap-1 font-medium ${
                      isDarkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-amber-700 hover:text-amber-600'
                    }`}
                  >
                    Read Full Article <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reviews Stats Section */}
        <div className="mb-12">
          <h2 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
            Trusted Solar Product Reviews
          </h2>
          <div className={`p-8 rounded-lg shadow-xl ${isDarkMode ? 'bg-blue-800' : 'bg-amber-800'}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="flex flex-col items-center">
                <div className="text-4xl font-bold text-white mb-2">500+</div>
                <p className="text-gray-300">Verified Reviews</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl font-bold text-white mb-2">4.8</div>
                <div className="mb-2">
                  <StarRating rating={4.8} />
                </div>
                <p className="text-gray-300">Average Rating</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl font-bold text-white mb-2">98%</div>
                <p className="text-gray-300">Customer Satisfaction</p>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mb-8">
              <p className="text-gray-200 text-lg mb-4">
                Make informed decisions with authentic reviews from South African solar users. Our community shares real experiences with solar panels, inverters, and batteries.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-gray-300 text-sm mb-6">
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1 text-green-400" />
                  Verified Purchases
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1 text-green-400" />
                  Local Experiences
                </span>
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-1 text-green-400" />
                  Load Shedding Solutions
                </span>
              </div>
            </div>
            <div className="text-center">
              <Link
                to="/reviews"
                className={`
                  inline-block px-8 py-4 rounded-lg font-semibold text-lg shadow-lg
                  ${isDarkMode
                    ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-300'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }
                  transition duration-200 transform hover:scale-105
                `}
              >
                Explore All Solar Reviews
              </Link>
            </div>
          </div>
        </div>

        {/* Customer Reviews Slider */}
        <div className="mb-12">
          <h2 className={`text-3xl font-bold mb-8 text-center ${isDarkMode ? 'text-yellow-400' : 'text-amber-800'}`}>
            What Our Customers Say Across South Africa
          </h2>
          <div className="relative">
            <div className={`p-8 rounded-lg shadow-xl ${isDarkMode ? 'bg-blue-800' : 'bg-amber-800'} transition-all duration-300 ease-in-out`}>
              {reviews.length > 0 && (
                <div className="flex flex-col items-center">
                  {reviews[currentReviewIndex].userAvatar && (
                    <img
                      src={reviews[currentReviewIndex].userAvatar}
                      alt={reviews[currentReviewIndex].userName}
                      className="w-20 h-20 rounded-full mb-4 border-4 border-yellow-400"
                    />
                  )}
                  <div className="mb-4">
                    <StarRating rating={reviews[currentReviewIndex].rating} />
                  </div>
                  <p className="text-gray-200 text-lg text-center mb-6 italic">
                    "{reviews[currentReviewIndex].comment}"
                  </p>
                  <div className="text-center">
                    <p className="text-white font-semibold text-lg">
                      {reviews[currentReviewIndex].userName}
                    </p>
                    <p className="text-yellow-400 text-sm mb-2">
                      {reviews[currentReviewIndex].location}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {new Date(reviews[currentReviewIndex].createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevReview}
              className={`
                absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4
                p-2 rounded-full
                ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-700 hover:bg-amber-600'}
                text-white
                transition-all duration-200
                shadow-lg
              `}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextReview}
              className={`
                absolute right-0 top-1/2 -translate-y-1/2 translate-x-4
                p-2 rounded-full
                ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-700 hover:bg-amber-600'}
                text-white
                transition-all duration-200
                shadow-lg
              `}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Review Indicators */}
            <div className="flex justify-center mt-6 space-x-2">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentReviewIndex(index)}
                  className={`
                    w-2 h-2 rounded-full transition-all duration-200
                    ${currentReviewIndex === index
                      ? isDarkMode ? 'bg-yellow-400' : 'bg-amber-500'
                      : isDarkMode ? 'bg-blue-600' : 'bg-amber-300'
                    }
                  `}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/reviews"
              className={`
                inline-block px-6 py-3 rounded-lg font-semibold
                ${isDarkMode
                  ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-300'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
                }
                transition duration-200
              `}
            >
              View All Reviews
            </Link>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mb-12">
          <div className={`text-center py-12 px-4 rounded-lg shadow-xl ${isDarkMode ? 'bg-blue-800' : 'bg-amber-800'}`}>
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Solar Journey?
            </h2>
            <p className="text-gray-200 mb-8 max-w-2xl mx-auto">
              Let us help you find the perfect solar solution for your needs
            </p>
            <Link
              to="/contact"
              className={`
                inline-block px-8 py-3 rounded-lg font-semibold text-white shadow-lg
                ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-600 hover:bg-amber-700'}
                transition duration-200
              `}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;